import { useState, useRef, useEffect } from 'react';

/**
 * The ⓘ next to a stat label.
 *
 * Every count card in the admin panel carries one, because almost every number
 * here is narrower than its label sounds: "users" means reader accounts unless
 * it says otherwise, "authors" means catalogue rows in one place and accounts
 * in another, "books" excludes pending and removed. A number without a stated
 * denominator has been read wrong at least twice, so the tooltip is not
 * decoration — it is the definition.
 *
 * Say what is counted, and say what is left out. "Readers and authors" is not
 * enough; "reader accounts, excluding the 66 authors who never made one" is.
 *
 * Mouse: opens on hover. Keyboard: opens on focus. Touch: a tap opens it and a
 * second tap, or a tap anywhere else, closes it. A touch tap is handled on its
 * own because it also fires a mouse-enter, a focus and a click, which would
 * otherwise open it and shut it again in the same tap. The hit area is padded
 * out to about 44px and pulled back with a negative margin, so the glyph does
 * not move the label beside it.
 */
export default function InfoTip({ text, width = 240 }) {
  const [show, setShow] = useState(false);
  const wrap = useRef(null);

  // A tap anywhere outside closes it (there is no mouse-leave on a touch screen).
  useEffect(() => {
    if (!show) return undefined;
    const away = (e) => { if (wrap.current && !wrap.current.contains(e.target)) setShow(false); };
    document.addEventListener('pointerdown', away);
    return () => document.removeEventListener('pointerdown', away);
  }, [show]);

  return (
    <span ref={wrap} style={{ position: 'relative', display: 'inline-block' }}>
      <span
        role="button"
        tabIndex={0}
        aria-label={text}
        onPointerEnter={e => { if (e.pointerType === 'mouse') setShow(true); }}
        onPointerLeave={e => { if (e.pointerType === 'mouse') setShow(false); }}
        onPointerDown={e => { if (e.pointerType !== 'mouse') setShow(s => !s); }}
        onFocus={e => { if (e.currentTarget.matches(':focus-visible')) setShow(true); }}
        onBlur={() => setShow(false)}
        onKeyDown={e => { if (e.key === 'Escape') setShow(false); }}
        style={{
          fontSize: 11, color: '#a8a29e', cursor: 'help', userSelect: 'none',
          outlineOffset: 2, display: 'inline-block', padding: 16, margin: -16,
        }}
      >ⓘ</span>
      {show && (
        <span style={{
          position: 'absolute', bottom: '130%', left: '50%', transform: 'translateX(-50%)',
          background: '#292524', color: '#fff', fontSize: 11, lineHeight: 1.55,
          padding: '8px 11px', borderRadius: 8, whiteSpace: 'pre-wrap',
          width, zIndex: 999, boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
          pointerEvents: 'none', textAlign: 'left', fontWeight: 400,
          textTransform: 'none', letterSpacing: 0,
        }}>{text}</span>
      )}
    </span>
  );
}
